/**
 * Mobile metro menu tile/link definitions (levels 1–3).
 * Access rules mirror desktop NavigationBar / sync_menus.py.
 */
import { LiaProjectDiagramSolid, LiaFileContractSolid } from 'react-icons/lia';
import { FaBoreHole, FaGears, FaCompassDrafting } from 'react-icons/fa6';
import { FaBroadcastTower } from 'react-icons/fa';
import { HiOutlineDocumentReport } from 'react-icons/hi';
import { HiOutlineChatBubbleLeftRight, HiOutlineMap } from 'react-icons/hi2';
import { BsFuelPump } from 'react-icons/bs';
import { GiSoundWaves } from 'react-icons/gi';
import { PiHouseLineFill } from 'react-icons/pi';
import { GrGroup } from 'react-icons/gr';
import { MdOutlineBusinessCenter, MdOutlineInventory, MdHealthAndSafety } from 'react-icons/md';
import { IoSettingsOutline } from 'react-icons/io5';
import { IoMdHelpCircleOutline } from 'react-icons/io';
import {
  buildAdministrationMetroSubSections,
  hasAdministrationMetroAccess,
} from '../adminNavConfig';
import { PROJECT_CHILD_ROUTES } from '../projects/projectNavConfig';
import { HR_CHILD_ROUTES } from '../hr/hrNavConfig';

export function createFieldInventoryMetroTile(section, fieldInventoryDirectPath) {
  return {
    id: 'inventory-field-entry',
    icon: MdOutlineInventory,
    title: 'Field inventory',
    section,
    color: '#0f2238',
    ...(fieldInventoryDirectPath ? { directPath: fieldInventoryDirectPath } : {}),
  };
}

export function buildMainMenus(ctx) {
  const {
    isProjectPersonnelOnly,
    isJmOperatorOnly,
    hasMenuAccess,
    hasMenuAccessOrChild,
    showCompanyMenu,
    showFieldInventoryMetroCard,
    fieldInventoryMetroTile,
    hasInventoryAccess,
    showGenericInventoryMetroTile,
    hasProjects,
    activeProject,
    isAdmin,
    isDemoProjectBootstrap,
    isPlatformDeveloper,
    hasSurvey,
    hasDrilling,
    hasRecording,
    hasBaseCamp,
    showHelicopterOpsMenu,
    tenderAccess,
  } = ctx;

const menus = [];

if (isJmOperatorOnly) {
  if (hasMenuAccess('/projects') || hasMenuAccessOrChild('/projects', PROJECT_CHILD_ROUTES)) {
    menus.push({
      id: 'projects',
      icon: LiaProjectDiagramSolid,
      title: 'Project',
      section: 'company',
      color: '#122640',
    });
  }
  if (hasMenuAccess('/operations/jm')) {
    menus.push({
      id: 'jm-hub',
      icon: FaBroadcastTower,
      title: 'JM Dashboard',
      section: 'project-personnel',
      color: '#2679bb',
      directPath: '/operations/jm',
    });
  }
  if (hasMenuAccess('/operations/jm/register')) {
    menus.push({
      id: 'jm-register',
      icon: FaBroadcastTower,
      title: 'JM Register',
      section: 'project-personnel',
      color: '#1a5f8f',
      directPath: '/operations/jm/register',
    });
  }
  return menus;
}

if (isProjectPersonnelOnly) {
  // Only the assigned module cards: Drill Progress Map, Operations Chat, + New Crew Report.
  // Renamed 2026-05-25 — drilling-specific tiles moved out of /operations (Incident 15).
  if (hasMenuAccess('/drilling/progress-map')) {
    menus.push({
      id: 'drilling-progress-map',
      icon: HiOutlineMap,
      title: 'Drill Progress Map',
      section: 'project-personnel',
      color: '#0f2238',
      directPath: '/drilling/progress-map'
    });
  }
  if (hasMenuAccess('/operations/fuel-control') || hasMenuAccess('/operations/fuel-control/supervisor')) {
    menus.push({
      id: 'fuel-control',
      icon: BsFuelPump,
      title: 'Fuel Control',
      section: 'project-personnel',
      color: '#8B4513',
      directPath: '/operations/fuel-control/supervisor'
    });
  }
  if (hasMenuAccess('/operations/jm/register') || hasMenuAccess('/operations/jm')) {
    menus.push({
      id: 'jm-register',
      icon: FaBroadcastTower,
      title: 'JM Register',
      section: 'project-personnel',
      color: '#2679bb',
      directPath: '/operations/jm/register'
    });
  }
  if (hasMenuAccess('/operations/chat')) {
    menus.push({
      id: 'operations-chat',
      icon: HiOutlineChatBubbleLeftRight,
      title: 'Operations Chat',
      section: 'project-personnel',
      color: '#1a3355',
      directPath: '/operations/chat'
    });
  }
  if (hasMenuAccess('/drilling/crew-report-field')) {
    menus.push({
      id: 'crew-report-field',
      icon: HiOutlineDocumentReport,
      title: '+ New Crew Report',
      section: 'project-personnel',
      color: '#122640',
      directPath: '/drilling/crew-report-field'
    });
  }
  if (showFieldInventoryMetroCard) {
    menus.push(fieldInventoryMetroTile('project-personnel'));
  }
  if (hasInventoryAccess && showGenericInventoryMetroTile) {
    menus.push({
      id: 'inventory',
      icon: MdOutlineInventory,
      title: 'Inventory',
      section: 'project-personnel',
      color: '#0f2238',
    });
  }
  return menus;
}

// Company Section
// Tenders come before Projects: a bid exists before the project does. The tile
// only appears for people holding a live grant on some tender — same rule as the
// desktop sidebar, or the tile would survive here after being hidden there.
if (tenderAccess?.hasAccess
  && (hasMenuAccess('/tenders') || hasMenuAccess('/tenders/archive'))) {
  menus.push({
    id: 'tenders',
    icon: LiaFileContractSolid,
    title: 'Tenders',
    section: 'company',
    color: '#122640'
  });
}

if (hasMenuAccessOrChild('/projects', PROJECT_CHILD_ROUTES)) {
  menus.push({
    id: 'projects',
    icon: LiaProjectDiagramSolid,
    title: 'Project',
    section: 'company',
    color: '#122640'
  });
}

// Field inventory: avoid duplicate tile — with an active project it lives under PROJECT (next to Field Ops); otherwise COMPANY
if (showCompanyMenu('inventory') && showFieldInventoryMetroCard && !activeProject) {
  menus.push(fieldInventoryMetroTile('company'));
}

if (hasAdministrationMetroAccess({
  showCompanyMenu,
  isAdmin,
  hasMenuAccessOrChild,
  hasInventoryAccess,
  showGenericInventoryMetroTile,
})) {
  menus.push({
    id: 'administration',
    icon: MdOutlineBusinessCenter,
    title: 'Administration',
    section: 'company',
    color: '#1a3355',
  });
}

if (showCompanyMenu('hr') && hasMenuAccessOrChild('/hr', HR_CHILD_ROUTES)) {
  menus.push({
    id: 'hr',
    icon: GrGroup,
    title: 'HR',
    section: 'company',
    color: '#122640'
  });
}

if (isDemoProjectBootstrap || isPlatformDeveloper || hasMenuAccessOrChild('/settings', ['/master-data/clients', '/master-data/departments', '/master-data/roles', '/master-data/equipment-categories', '/master-data/equipment-items', '/admin/users', '/access-control', '/admin/backups', '/company/backups', '/settings/company', '/settings/currencies', '/settings/display']) || (!isDemoProjectBootstrap && !isProjectPersonnelOnly)) {
  menus.push({
    id: 'settings',
    icon: IoSettingsOutline,
    title: 'Settings',
    section: 'company',
    color: '#1a3355'
  });
}

if (showCompanyMenu('help') && hasMenuAccessOrChild('/help', ['/help/manual'])) {
  menus.push({
    id: 'help',
    icon: IoMdHelpCircleOutline,
    title: 'Help',
    section: 'company',
    color: '#0f2238'
  });
}

// Project Section (only if activeProject exists)
if (activeProject) {
  if (hasSurvey && hasMenuAccessOrChild('/survey', ['/survey/map', '/survey/planning', '/survey/production-reports', '/budgets/survey', '/budgets/survey/new', '/budgets/management?area=survey', '/dailies/survey/new', '/dailies/management?area=survey', '/summary/survey'])) {
    menus.push({
      id: 'survey',
      icon: FaCompassDrafting,
      title: 'Survey',
      section: 'project',
      color: '#122640'
    });
  }

  if (hasDrilling && hasMenuAccessOrChild('/drilling', [
    '/budgets/drilling',
    '/budgets/drilling/new',
    '/budgets/management?area=drilling',
    '/dailies/drilling/new',
    '/dailies/management?area=drilling',
    '/summary/drilling',
    '/drilling/sp-registry',
    '/drilling/crew-reports',
    '/drilling/crew-report-field',
    '/drilling/npt-log',
    '/drilling/progress-map',
    '/drilling/work-planning',
    '/drilling/work-projection',
    '/drilling/client-daily-report',
    '/drilling/cdr-archive',
    '/drilling/npt-report-archive',
  ])) {
    menus.push({
      id: 'drilling',
      icon: FaBoreHole,
      title: 'Drilling',
      section: 'project',
      color: '#1a3355'
    });
  }

  if (hasRecording && hasMenuAccessOrChild('/recording', ['/recording/map', '/recording/planning', '/recording/production-reports', '/budgets/recording', '/budgets/recording/new', '/budgets/management?area=recording', '/dailies/recording/new', '/dailies/management?area=recording', '/summary/recording'])) {
    menus.push({
      id: 'recording',
      icon: GiSoundWaves,
      title: 'Recording',
      section: 'project',
      color: '#0f2238'
    });
  }

  if (hasBaseCamp && hasMenuAccessOrChild('/basecamp', ['/budgets/basecamp', '/budgets/new', '/budgets/management?area=basecamp', '/dailies/basecamp/new', '/dailies/management?area=basecamp', '/summary/basecamp', '/basecamp/camp-layout'])) {
    menus.push({
      id: 'basecamp',
      icon: PiHouseLineFill,
      title: 'Base Camp',
      section: 'project',
      color: '#122640'
    });
  }

  // Direct-access tiles (one tap to route).
  if (hasMenuAccess('/operations/chat')) {
    menus.push({
      id: 'operations-chat',
      icon: HiOutlineChatBubbleLeftRight,
      title: 'Operations Chat',
      section: 'project',
      color: '#1a3355',
      directPath: '/operations/chat'
    });
  }
  if (hasMenuAccess('/drilling/progress-map')) {
    menus.push({
      id: 'drilling-progress-map',
      icon: HiOutlineMap,
      title: 'Drill Progress Map',
      section: 'project',
      color: '#0f2238',
      directPath: '/drilling/progress-map'
    });
  }
  if (showFieldInventoryMetroCard) {
    menus.push(fieldInventoryMetroTile('project'));
  }
  if (hasMenuAccess('/operations/fuel-control') || hasMenuAccess('/operations/fuel-control/supervisor')) {
    menus.push({
      id: 'fuel-control',
      icon: BsFuelPump,
      title: 'Fuel Control',
      section: 'project',
      color: '#8B4513',
      directPath: '/operations/fuel-control/supervisor'
    });
  }
  if (hasMenuAccess('/operations/jm/register') || hasMenuAccess('/operations/jm')) {
    menus.push({
      id: 'jm-register-project',
      icon: FaBroadcastTower,
      title: 'JM Register',
      section: 'project',
      color: '#2679bb',
      directPath: '/operations/jm/register'
    });
  }
  if (hasMenuAccess('/drilling/crew-report-field')) {
    menus.push({
      id: 'crew-report-field',
      icon: HiOutlineDocumentReport,
      title: '+ New Crew Report',
      section: 'project',
      color: '#122640',
      directPath: '/drilling/crew-report-field'
    });
  }

  // Operations (Fleet, Helicopter, etc.) tile only when user has access to those sub-modules, not just Chat/Field Ops
  const hasOtherOperationsAccess = hasMenuAccessOrChild('/operations', [
    '/operations/fleet',
    '/operations/fleet/registry',
    '/operations/fleet/categories',
    '/operations/fleet/daily',
    '/operations/fleet/maintenance',
    '/operations/jm',
    '/operations/jm/register',
    '/operations/jm/settings',
    ...(showHelicopterOpsMenu
      ? ['/operations/helicopter', '/operations/helicopter/new', '/operations/helicopter/registry']
      : []),
  ]);
  if (hasOtherOperationsAccess) {
    menus.push({
      id: 'operations',
      icon: FaGears,
      title: 'Operations',
      section: 'project',
      color: '#1a3355'
    });
  }

  if (hasMenuAccessOrChild('/qhse', ['/qhse?tab=personnel', '/qhse?tab=daily-logs', '/qhse?tab=env-health', '/qhse?tab=comments', '/qhse?tab=daily-report', '/qhse?tab=kpis', '/qhse?tab=settings', '/qhse/document-control', '/hr/hse-induction'])) {
    menus.push({
      id: 'qhse',
      icon: MdHealthAndSafety,
      title: 'QHSE',
      section: 'project',
      color: '#0f2238'
    });
  }

}

return menus;
}

export function buildSubSections(menuId, ctx) {
  const {
    activeProject,
    hasMenuAccess,
    hasProjects,
    isDemoProjectBootstrap,
    isSuperAdmin,
    isPlatformDeveloper,
    isAdmin,
    showHelicopterOpsMenu,
  } = ctx;

const sections = {
  tenders: [
    ...(hasMenuAccess('/tenders') ? [{ id: 'active', title: 'Active Tenders' }] : []),
    ...(hasMenuAccess('/tenders/archive') ? [{ id: 'archive', title: 'Tender Archive' }] : []),
  ],
  projects: [
    ...(!activeProject && hasMenuAccess('/projects') ? [{ id: 'new', title: '+ New Project' }] : []),
    ...(hasProjects ? [{ id: 'list', title: 'Project List' }] : []),
    ...(activeProject ? [{ id: 'active', title: `Active: ${activeProject.project_name}` }] : []),
    ...(activeProject && (hasMenuAccess('/master-budget') || hasMenuAccess('/budgets/management'))
      ? [{ id: 'budget', title: 'Budget' }]
      : []),
  ],
  finance: [
    { id: 'petty-cash', title: 'Petty Cash' },
    { id: 'corporate-expenses', title: 'Corporate Expenses' },
    { id: 'company-assets', title: 'Company assets' },
    { id: 'accounting', title: 'Accounting' }
  ],
  'inventory-field-entry': [
    ...(hasMenuAccess('/inventory/mobile') ? [{ id: 'field-list', title: 'List' }] : []),
    ...(hasMenuAccess('/inventory/new') ? [{ id: 'field-add', title: 'New asset' }] : []),
  ],
  inventory: [
    ...(hasMenuAccess('/inventory/dashboard') ||
      hasMenuAccess('/inventory/new') ||
      hasMenuAccess('/inventory/mobile') ||
      hasMenuAccess('/inventory/kits') ||
      hasMenuAccess('/inventory/parameters') ||
      hasMenuAccess('/inventory/vendors') ||
      hasMenuAccess('/inventory')
      ? [{ id: 'main', title: 'Inventory' }]
      : []),
    ...(hasMenuAccess('/inventory/kits') || hasMenuAccess('/inventory')
      ? [{ id: 'kits', title: 'Equipment Kits' }]
      : []),
    ...((hasMenuAccess('/inventory/parameters') ||
      hasMenuAccess('/inventory/vendors') ||
      hasMenuAccess('/inventory'))
      ? [{ id: 'config', title: 'Configuration' }]
      : []),
  ],
  // Las secciones siguen al flujo objetivo. «Company Personnel» y «Project
  // Personnel» eran el interruptor binario de `work_context` pintado en el
  // menu; la division Company/Project es centro de coste, no dos mundos.
  hr: [
    { id: 'recruitment', title: 'Recruitment' },
    { id: 'personnel', title: 'Personnel' },
    { id: 'timesheet', title: 'Timesheet' },
    { id: 'payroll', title: 'Payroll' },
    { id: 'reports', title: 'Reports & Analytics' },
    { id: 'config', title: 'Configuration' }
  ],
  settings: [
    { id: 'preferences', title: 'Preferences' },
    { id: 'master-data', title: 'Master Data' },
    ...(!isDemoProjectBootstrap && isSuperAdmin ? [{ id: 'system', title: 'System' }] : []),
    ...(!isDemoProjectBootstrap && isAdmin ? [{ id: 'organization', title: 'Organization' }] : []),
    ...(!isDemoProjectBootstrap && isPlatformDeveloper ? [{ id: 'platform', title: 'Platform' }] : []),
  ],
  help: [
    { id: 'resources', title: 'Resources' }
  ],
  'supply-chain': [
    ...(hasMenuAccess('/supply-chain/requisitions')
      ? [{ id: 'requisitions', title: 'Requisitions' }]
      : []),
    ...(hasMenuAccess('/supply-chain/purchase-orders')
      ? [{ id: 'purchase-orders', title: 'Purchase Orders' }]
      : []),
    ...((hasMenuAccess('/supply-chain/approval-inbox') || hasMenuAccess('/supply-chain/requisitions'))
      ? [{ id: 'approval-inbox', title: 'Approval Inbox' }]
      : []),
    ...(hasMenuAccess('/supply-chain/vendors')
      ? [{ id: 'vendors', title: 'Vendors / Suppliers' }]
      : []),
  ],
  catering: [
    ...(hasMenuAccess('/catering/estimator') ? [{ id: 'estimator', title: 'Cost Estimator' }] : []),
    ...(hasMenuAccess('/catering/food-items') || hasMenuAccess('/catering/rations')
      ? [{ id: 'catalog', title: 'Catalog' }]
      : []),
    ...(hasMenuAccess('/catering/estimates') ? [{ id: 'estimates', title: 'Saved Estimates' }] : []),
  ],
  survey: [
    { id: 'planning', title: 'Planning' },
    { id: 'operations', title: 'Operations' },
    { id: 'reports', title: 'Reports' }
  ],
  drilling: [
    { id: 'planning', title: 'Planning' },
    { id: 'operations', title: 'Operations' },
    { id: 'reports', title: 'Reports' }
  ],
  recording: [
    { id: 'planning', title: 'Planning' },
    { id: 'reports', title: 'Reports' }
  ],
  basecamp: [
    { id: 'planning', title: 'Planning' },
    { id: 'operations', title: 'Operations' },
    { id: 'reports', title: 'Reports' }
  ],
  operations: [
    { id: 'fleet-equip', title: 'Fleet & Equip' },
    ...(showHelicopterOpsMenu ? [{ id: 'transportation', title: 'Transportation' }] : []),
    { id: 'comms', title: 'Comms' }
  ],
  qhse: [
    { id: 'modules', title: 'QHSE Modules' }
  ],
  administration: buildAdministrationMetroSubSections(ctx),
};

  return sections[menuId] || [];
}

export function buildMenuItems(menuId, sectionId, ctx) {
  const {
    activeProject,
    hasApprovedBudget,
    showFieldInventoryMetroCard,
    hasMenuAccess,
    approvedPresence,
    showHelicopterOpsMenu,
    surveyBudgetPath,
    drillingBudgetPath,
    recordingBudgetPath,
    basecampBudgetPath,
  } = ctx;

const items = {
  tenders: {
    active: [
      { title: 'Active Tenders', path: '/tenders' }
    ],
    archive: [
      { title: 'Tender Archive', path: '/tenders/archive' }
    ],
  },
  projects: {
    new: [
      { title: '+ New Project', path: '/projects/portfolio' }
    ],
    list: [
      { title: 'Project List', path: '/projects/portfolio' }
    ],
    active: activeProject
      ? [
          ...(hasApprovedBudget
            ? [{ title: 'Dashboard', path: `/projects/${activeProject.project_id}/dashboard` }]
            : []),
          { title: 'Project Scope', path: `/projects/${activeProject.project_id}/scope` },
          ...(hasMenuAccess('/projects/tender')
            ? [{ title: 'Approved Tender', path: `/projects/${activeProject.project_id}/tender` }]
            : []),
          { title: 'Schedule (Gantt)', path: `/projects/${activeProject.project_id}/schedule` },
          { title: 'Progress Map', path: '/projects/combined-progress' },
        ]
      : [],
    budget: activeProject
      ? [
          ...(hasApprovedBudget && hasMenuAccess('/master-budget')
            ? [{ title: 'Master Budget', path: '/master-budget' }]
            : []),
          ...(hasMenuAccess('/budgets/management')
            ? [{ title: 'Budget Management', path: '/budgets/management' }]
            : []),
        ]
      : [],
  },
  finance: {
    'petty-cash': [
      { title: 'Petty Cash Dashboard', path: '/finance/petty-cash' },
      { title: 'New Petty Cash', path: '/finance/petty-cash/new' },
      { title: 'Petty Cash Funds', path: '/finance/petty-cash/funds' },
      { title: 'Petty Cash Reports', path: '/finance/petty-cash/reports' },
      { title: 'Monthly Report', path: '/finance/petty-cash/reports/monthly' }
    ],
    'corporate-expenses': [
      { title: 'Expenses List', path: '/finance/corporate-expenses' },
      { title: 'New Expense', path: '/finance/corporate-expenses/new' }
    ],
    'company-assets': [
      { title: 'Company assets (recovery)', path: '/finance/company-assets' },
      { title: 'New company asset', path: '/finance/company-assets/new' }
    ],
    accounting: [
      { title: 'Chart of Accounts', path: '/finance/coa' },
      { title: 'Exchange Rates', path: '/finance/exchange-rates' },
    ]
  },
  'inventory-field-entry': {
    'field-list': [{ title: 'Field entry (mobile hub)', path: '/inventory/mobile' }],
    'field-add': [{ title: 'New asset', path: '/inventory/mobile/new' }],
  },
  inventory: {
    main: [
      ...(!showFieldInventoryMetroCard &&
      (hasMenuAccess('/inventory/new') || hasMenuAccess('/inventory'))
        ? [{ title: 'New asset', path: '/inventory/mobile/new' }]
        : []),
      ...(hasMenuAccess('/inventory/dashboard') || hasMenuAccess('/inventory')
        ? [{ title: 'Inventory Dashboard', path: '/inventory' }]
        : []),
    ],
    kits: [
      { title: 'Equipment Kits', path: '/inventory/kits' },
      ...(!showFieldInventoryMetroCard &&
      (hasMenuAccess('/inventory/mobile') || hasMenuAccess('/inventory'))
        ? [{ title: 'Field entry (mobile hub)', path: '/inventory/mobile' }]
        : []),
    ],
    config: [
      { title: 'Parameters', path: '/inventory/parameters' },
      { title: 'Vendors / Suppliers', path: '/inventory/vendors' }
    ]
  },
  hr: {
    recruitment: [
      { title: 'Recruitment', path: '/hr/candidates' },
      { title: '+ New Candidate', path: '/hr/candidates/new' },
      { title: 'Blacklist', path: '/hr/blacklist' },
      { title: 'HSE Induction', path: '/hr/hse-induction' }
    ],
    personnel: [
      { title: 'Personnel', path: '/hr/personnel' },
      { title: 'Available for Projects', path: '/hr/company/available' },
      ...(hasMenuAccess('/hr/key-personnel')
        ? [{ title: 'Key Personnel CVs', path: '/hr/key-personnel' }]
        : [])
    ],
    timesheet: [
      { title: 'Timesheet', path: '/hr/timesheet/month' },
      { title: 'Timesheet Entries', path: '/hr/timesheet/entries' }
    ],
    payroll: [
      { title: 'Corporate Payroll', path: '/hr/payroll/company' },
      ...(activeProject ? [{ title: 'Project Payroll', path: '/hr/payroll/project' }] : [])
    ],
    reports: [
      { title: 'Assignment History', path: '/hr/reports/assignments' },
      { title: 'Workforce Statistics', path: '/hr/reports/statistics' },
      { title: 'Monthly Reports', path: '/hr/timesheet/reports' }
    ],
    config: [
      { title: 'Timesheet Codes', path: '/hr/timesheet/codes' },
      { title: 'HR Settings', path: '/hr/configuration' }
    ]
  },
  settings: {
    preferences: [
      { title: 'Display', path: '/settings/display' },
    ],
    'master-data': [
      { title: 'Clients', path: '/master-data/clients' },
      { title: 'Departments', path: '/master-data/departments' },
      { title: 'Job Positions', path: '/master-data/roles' },
      { title: 'Equipment Categories', path: '/master-data/equipment-categories' },
      { title: 'Equipment Items', path: '/master-data/equipment-items' }
    ],
    system: [
      { title: 'Users Management', path: '/admin/users' },
      { title: 'Access Control', path: '/access-control' },
      { title: 'Backups', path: '/admin/backups' },
      { title: 'Company Backup', path: '/company/backups' }
    ],
    organization: [
      { title: 'Company Settings', path: '/settings/company' },
      { title: 'Currencies', path: '/settings/currencies' },
    ],
    platform: [
      { title: 'Access Developer', path: '/platform' },
    ],
  },
  help: {
    resources: [
      { title: 'FAQ', path: '/help/faq' },
      { title: 'User Manual', path: '/help/manual' },
    ],
  },
  'supply-chain': {
    requisitions: [
      { title: 'Requisitions List', path: '/supply-chain/requisitions' },
      { title: 'New Requisition', path: '/supply-chain/requisitions/new' }
    ],
    'purchase-orders': [
      { title: 'Purchase Orders List', path: '/supply-chain/purchase-orders' },
      { title: 'New Purchase Order', path: '/supply-chain/purchase-orders/new' }
    ],
    'approval-inbox': [
      { title: 'Approval Inbox', path: '/supply-chain/approval-inbox' }
    ],
    vendors: [
      { title: 'Vendors / Suppliers', path: '/supply-chain/vendors' }
    ]
  },
  catering: {
    estimator: [
      { title: 'Cost Estimator', path: '/catering/estimator' }
    ],
    catalog: [
      { title: 'Food Items', path: '/catering/food-items' },
      { title: 'Ration Templates', path: '/catering/rations' }
    ],
    estimates: [
      { title: 'Saved Estimates', path: '/catering/estimates' }
    ]
  },
  survey: {
    planning: [
      { title: 'Budget', path: surveyBudgetPath || '/budgets/survey/new' },
      { title: '📋 Field Planning', path: '/survey/planning' },
      { title: 'New Daily', path: '/dailies/survey/new' },
      { title: 'Dailies Management', path: '/dailies/management?area=survey' }
    ],
    operations: [
      { title: '📍 Map', path: '/survey/map' },
      { title: '📈 Production Reports', path: '/survey/production-reports' }
    ],
    reports: [
      { title: 'Summary', path: '/summary/survey' }
    ]
  },
  drilling: {
    planning: [
      ...(approvedPresence?.drilling && hasMenuAccess('/drilling/dashboard')
        ? [{ title: 'Dashboard', path: '/drilling/dashboard' }]
        : []),
      { title: 'Budget', path: drillingBudgetPath || '/budgets/drilling/new' },
      { title: 'New Daily', path: '/dailies/drilling/new' },
      { title: 'Dailies Management', path: '/dailies/management?area=drilling' }
    ],
    operations: [
      { title: 'SP Registry', path: '/drilling/sp-registry' },
      { title: 'Crew Reports', path: '/drilling/crew-reports' },
      { title: '+ New Crew Report', path: '/drilling/crew-report-field' },
      { title: 'NPT Log', path: '/drilling/npt-log' },
      { title: '📍 Drill Progress Map', path: '/drilling/progress-map' },
      { title: '📋 Drill Work Planning', path: '/drilling/work-planning' },
      { title: '📈 Work Projection', path: '/drilling/work-projection' }
    ],
    reports: [
      ...(approvedPresence.drilling ? [{ title: 'Summary', path: '/summary/drilling' }] : []),
      { title: 'Client Daily Report', path: '/drilling/client-daily-report' },
      { title: 'CDR Archive', path: '/drilling/cdr-archive' },
      { title: 'NPT Report Archive', path: '/drilling/npt-report-archive' }
    ]
  },
  recording: {
    planning: [
      { title: 'Budget', path: recordingBudgetPath || '/budgets/recording/new' },
      { title: '📋 Field Planning', path: '/recording/planning' },
      { title: 'New Daily', path: '/dailies/recording/new' },
      { title: 'Dailies Management', path: '/dailies/management?area=recording' }
    ],
    operations: [
      { title: '📍 Map', path: '/recording/map' },
      { title: '📈 Production Reports', path: '/recording/production-reports' }
    ],
    reports: [
      { title: 'Summary', path: '/summary/recording' }
    ]
  },
  basecamp: {
    planning: [
      { title: 'Budget', path: basecampBudgetPath || '/budgets/basecamp/new' },
      { title: 'New Daily', path: '/dailies/basecamp/new' },
      { title: 'Dailies Management', path: '/dailies/management?area=basecamp' }
    ],
    operations: [
      { title: 'Camp Layout', path: '/basecamp/camp-layout' }
    ],
    reports: [
      { title: 'Summary', path: '/summary/basecamp' }
    ]
  },
  operations: {
    'fleet-equip': [
      { title: '🚛 Project Fleet', path: '/operations/fleet' },
      { title: '📋 Asset Registry', path: '/operations/fleet/registry' },
      { title: '🏷️ Fleet Categories', path: '/operations/fleet/categories' },
      { title: '📅 Daily Log', path: '/operations/fleet/daily' },
      { title: '🔧 Maintenance', path: '/operations/fleet/maintenance' }
    ],
    ...(showHelicopterOpsMenu
      ? {
          transportation: [
            { title: 'Helicopter Ops', path: '/operations/helicopter' },
            { title: 'New Configuration', path: '/operations/helicopter/new' },
            { title: '🚁 Helicopter Registry', path: '/operations/helicopter/registry' }
          ]
        }
      : {}),
    // Drilling-specific tiles (Progress Map, Work Planning, Work Projection) moved
    // to the Drilling menu on 2026-05-25 (Incident 15).
    'radio-ops': [
      { title: '📻 JM', path: '/operations/jm' },
      { title: 'Register OUT/IN', path: '/operations/jm/register' },
      { title: 'JM Settings', path: '/operations/jm/settings' }
    ],
    comms: [
      { title: '💬 Operations Chat', path: '/operations/chat' }
    ]
  },
  qhse: {
    modules: [
      { title: 'Personnel', path: '/qhse?tab=personnel' },
      { title: 'Daily Logs', path: '/qhse?tab=daily-logs' },
      { title: 'Environment & Health', path: '/qhse?tab=env-health' },
      { title: 'Comments & Meetings', path: '/qhse?tab=comments' },
      { title: 'Daily Report', path: '/qhse?tab=daily-report' },
      { title: 'KPI Dashboard', path: '/qhse?tab=kpis' },
      { title: 'Document Control', path: '/qhse/document-control' },
      { title: 'HSE Induction', path: '/hr/hse-induction' },
      { title: '⚙ Settings', path: '/qhse?tab=settings' }
    ]
  },
};

  if (menuId === 'administration') {
    const moduleMap = {
      finance: 'finance',
      'supply-chain': 'supply-chain',
      inventory: 'inventory',
      catering: 'catering',
    };
    const childModuleId = moduleMap[sectionId];
    if (!childModuleId) return [];
    return buildSubSections(childModuleId, ctx).flatMap(
      (sec) => items[childModuleId]?.[sec.id] || [],
    );
  }

  return items[menuId]?.[sectionId] || [];
}
